import { useCallback, useEffect, useRef, useState } from "react";
import {
  getNafathStatus,
  initiateNafath,
  isNafathCompleted,
  type NafathIdentity,
  type NafathStatusResponse,
} from "../../services/nafathService";

const INITIAL_POLL_DELAY_MS = 20_000;
const POLL_MIN_MS = 5_000;
const POLL_MAX_MS = 10_000;
const MAX_POLL_DURATION_MS = 90_000;

function randInt(min: number, max: number) {
  return Math.floor(min + Math.random() * (max - min + 1));
}

const onlyDigits = (s: string) => (s || "").replace(/\D/g, "");

export type NafathVerificationResult = {
  transId: string;
  requestId?: string;
  identity: NafathIdentity;
  status: NafathStatusResponse;
};

export type UseNafathVerificationOptions = {
  /** National ID for initiate/status (digits only internally). */
  nationalId: string;
  onCompleted?: (result: NafathVerificationResult) => void | Promise<void>;
};

type StatusContext = {
  transId: string;
  random: string;
  nationalId: string;
};

export function useNafathVerification({ nationalId, onCompleted }: UseNafathVerificationOptions) {
  const [randomCode, setRandomCode] = useState<string | null>(null);
  const [transId, setTransId] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [lastStatus, setLastStatus] = useState<NafathStatusResponse | null>(null);
  const [identity, setIdentity] = useState<NafathIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const pollActiveRef = useRef(false);
  const contextRef = useRef<StatusContext>({ transId: "", random: "", nationalId: "" });
  const pollStartedAtRef = useRef(0);
  const pollTimeoutIdRef = useRef<number | null>(null);
  const scheduleRef = useRef<((ms: number) => void) | null>(null);
  const clearTimerRef = useRef<(() => void) | null>(null);

  const syncContext = useCallback((ctx: Partial<StatusContext>) => {
    contextRef.current = { ...contextRef.current, ...ctx };
  }, []);

  useEffect(() => {
    syncContext({ nationalId: onlyDigits(nationalId) });
  }, [nationalId, syncContext]);

  useEffect(() => {
    if (transId) syncContext({ transId });
  }, [transId, syncContext]);

  useEffect(() => {
    if (randomCode != null) syncContext({ random: String(randomCode) });
  }, [randomCode, syncContext]);

  const clearTimer = useCallback(() => {
    if (pollTimeoutIdRef.current != null) {
      window.clearTimeout(pollTimeoutIdRef.current);
      pollTimeoutIdRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(
    async (ctx?: Partial<StatusContext>) => {
      const merged = { ...contextRef.current, ...ctx };
      const tid = merged.transId?.trim();
      const rnd = merged.random != null ? String(merged.random).trim() : "";
      const nin = merged.nationalId?.trim() || onlyDigits(nationalId);

      if (!tid) throw new Error("معرّف طلب نفاذ غير متوفر.");
      if (!rnd) throw new Error("رقم التحقق من نفاذ غير متوفر. أعد بدء نفاذ.");

      const s = await getNafathStatus({
        transId: tid,
        nationalId: nin,
        random: rnd,
      });
      setLastStatus(s);

      if (isNafathCompleted(s) && s.identity) {
        setIdentity(s.identity);
        pollActiveRef.current = false;
        setPolling(false);
        clearTimer();
        const result: NafathVerificationResult = {
          transId: tid,
          requestId: s.requestId || requestId || undefined,
          identity: s.identity,
          status: s,
        };
        await onCompletedRef.current?.(result);
        return true;
      }
      return false;
    },
    [clearTimer, nationalId, requestId]
  );

  useEffect(() => {
    if (!polling) return;
    if (!contextRef.current.transId) return;

    let cancelled = false;
    pollActiveRef.current = true;
    pollStartedAtRef.current = Date.now();

    const clearTimerLocal = () => {
      if (pollTimeoutIdRef.current != null) {
        window.clearTimeout(pollTimeoutIdRef.current);
        pollTimeoutIdRef.current = null;
      }
    };

    const schedule = (ms: number) => {
      clearTimerLocal();
      pollTimeoutIdRef.current = window.setTimeout(tick, ms);
    };

    scheduleRef.current = schedule;
    clearTimerRef.current = clearTimerLocal;

    const tick = async () => {
      if (cancelled || !pollActiveRef.current || !contextRef.current.transId) return;
      const elapsed = Date.now() - pollStartedAtRef.current;
      if (elapsed >= MAX_POLL_DURATION_MS) {
        setInfo(null);
        setError("انتهت مهلة التحقق عبر نفاذ. اضغط «إعادة المحاولة» لإنشاء طلب جديد.");
        pollActiveRef.current = false;
        setPolling(false);
        clearTimerLocal();
        return;
      }
      try {
        const done = await refreshStatus();
        if (done) return;
        setError(null);
        setInfo("في انتظار الموافقة في تطبيق نفاذ...");
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        const fatal =
          /random|transId|معرّف|رقم التحقق/i.test(msg) &&
          !/waiting|انتظار/i.test(msg);
        if (fatal) {
          setError(msg);
          pollActiveRef.current = false;
          setPolling(false);
          clearTimerLocal();
          return;
        }
        setInfo("جاري التحقق من حالة نفاذ...");
      }
      schedule(randInt(POLL_MIN_MS, POLL_MAX_MS));
    };

    schedule(INITIAL_POLL_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimerLocal();
      scheduleRef.current = null;
      clearTimerRef.current = null;
    };
  }, [polling, refreshStatus]);

  const reset = useCallback(() => {
    pollActiveRef.current = false;
    setPolling(false);
    clearTimerRef.current?.();
    clearTimer();
    contextRef.current = { transId: "", random: "", nationalId: onlyDigits(nationalId) };
    setRandomCode(null);
    setTransId("");
    setRequestId(null);
    setLastStatus(null);
    setIdentity(null);
    setError(null);
    setInfo(null);
  }, [clearTimer, nationalId]);

  const start = useCallback(async () => {
    setError(null);
    setInfo(null);
    pollActiveRef.current = false;
    setPolling(false);
    clearTimerRef.current?.();
    clearTimer();
    setRandomCode(null);
    setTransId("");
    setRequestId(null);
    setLastStatus(null);
    setIdentity(null);

    const nin = onlyDigits(nationalId);
    if (nin.length < 6) {
      setError("يرجى إدخال رقم هوية صالح.");
      return;
    }

    setBusy(true);
    try {
      const res = await initiateNafath({ nationalId: nin });
      if (!res.transId) {
        setError("لم يُرجع الخادم transId.");
        return;
      }
      if (res.random == null || String(res.random).trim() === "") {
        setError("لم يُرجع الخادم رقم التحقق (random).");
        return;
      }

      const tid = res.transId;
      const rnd = String(res.random);
      const ctx: StatusContext = { transId: tid, random: rnd, nationalId: nin };
      contextRef.current = ctx;

      setRequestId(res.requestId ?? null);
      setTransId(tid);
      setRandomCode(rnd);
      setInfo("تم إنشاء الطلب. افتح تطبيق نفاذ وأدخل الرقم ثم وافق على الطلب.");

      pollActiveRef.current = true;
      setPolling(true);

      try {
        await refreshStatus(ctx);
      } catch {
        // Still WAITING or transient — polling continues on schedule.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [clearTimer, nationalId, refreshStatus]);

  const stopPolling = useCallback(() => {
    pollActiveRef.current = false;
    setPolling(false);
    clearTimerRef.current?.();
    clearTimer();
  }, [clearTimer]);

  const checkNow = useCallback(async () => {
    if (!contextRef.current.transId) return;
    setError(null);
    setInfo(null);
    try {
      const done = await refreshStatus();
      if (done) return;
      setInfo("في انتظار الموافقة في تطبيق نفاذ...");
      pollActiveRef.current = true;
      setPolling(true);
      scheduleRef.current?.(randInt(POLL_MIN_MS, POLL_MAX_MS));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [refreshStatus]);

  return {
    randomCode,
    transId,
    requestId,
    lastStatus,
    identity,
    error,
    info,
    busy,
    polling,
    start,
    reset,
    stopPolling,
    checkNow,
  };
}
